import axios from 'axios';
import calculateStatus from '../hooks/useComputeStatus';
import { TOKEN_EXPIRED, TIMEOUT } from '../hooks/useAPIHelper';
import { reactiveRowToJob, applyPortalStatus } from './reactiveCallPagesShape';

/**
 * A customer's reactive calls from paged files served from SQL (PORTAL-199), for Home's recent list and its SLA
 * indicator. THERE IS NO FALLBACK TO THE LIVE PRONTO CALL, and that is the point: Home must not call Pronto live.
 *
 * WHEN IT CANNOT ANSWER IT THROWS TIMEOUT, which Home already shows as its "could not load" state, and it throws
 * the expired-token words for an expired Pronto token, exactly as the live call does. A set that is still being
 * built (HTTP 202) is waited for up to MAX_BUILD_WAIT_MS; past that it is TIMEOUT too, and the next load finds the
 * finished set because the build carries on.
 *
 * THE BROWSER FETCHES THE PAGES ITSELF, with no credential: each URL is signed, read-only and lasts about 30
 * minutes. fetch() is used instead of axios so nothing in the portal's axios defaults can reach the storage
 * account. Pages are read in parallel and kept in the order they were cut, which is newest first.
 *
 * THE LIST IS THE WHOLE CUSTOMER'S, NOT THE PERSON'S. The caller filters it by the contracts the person may see
 * (filterDataByContract), as it does for the live list.
 */

const PAGES_URL = '/.netlify/functions/xServiceGetReactiveCallPages';

const MAX_BUILD_WAIT_MS = 25000;

interface ManifestFile {
  n?: number;
  url: string;
  rows?: number;
}

interface Manifest {
  files: ManifestFile[];
  committedUtc?: string | null;
}

// The time the set this browser last read was committed, by customer, so Home can show "data as at".
const lastCommitted: Record<string, string | null> = {};

export const reactiveCallsAsOfFor = (customerId: string): string | null => lastCommitted[customerId] ?? null;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function readPage(file: ManifestFile): Promise<any[]> {
  const reply = await fetch(file.url, { method: 'GET', credentials: 'omit' });

  if (!reply.ok) {
    throw new Error(`page ${file.n ?? '?'} answered HTTP ${reply.status}`);
  }

  const rows = await reply.json();

  if (!Array.isArray(rows)) {
    throw new Error(`page ${file.n ?? '?'} is not a list`);
  }

  return rows;
}

const GetReactiveCallPages = async (token: string, customerId: string): Promise<any[]> => {
  const started = Date.now();
  const body = `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`;

  lastCommitted[customerId] = null;

  try {
    let manifest: Manifest | null = null;

    while (manifest === null) {
      const reply = await axios.post(PAGES_URL, body, {
        headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token },
        validateStatus: (status) => status === 200 || status === 202,
      });

      if (reply.status === 200) {
        manifest = reply.data as Manifest;
        break;
      }

      const retryMs = Math.max(1, Number(reply.data?.retryAfterSeconds ?? 5)) * 1000;

      if (Date.now() - started + retryMs > MAX_BUILD_WAIT_MS) {
        console.log('Reactive call pages still building - not calling Pronto live');
        throw TIMEOUT;
      }

      await wait(retryMs);
    }

    const pages = await Promise.all(manifest.files.map((file) => readPage(file)));

    // A page that disagrees with the manifest was damaged or cut short; showing it would show fewer calls with
    // nothing to say so.
    pages.forEach((rows, index) => {
      const expected = manifest!.files[index].rows;

      if (expected !== undefined && rows.length !== expected) {
        throw new Error(`page ${manifest!.files[index].n ?? index + 1} holds ${rows.length} rows, the manifest says ${expected}`);
      }
    });

    lastCommitted[customerId] = manifest.committedUtc ?? null;

    const jobs = calculateStatus(pages.flat().map(reactiveRowToJob) as any).map(applyPortalStatus);

    console.log(`Reactive call pages: ${jobs.length} rows from ${pages.length} page(s) in ${Date.now() - started} ms`);

    return jobs;
  } catch (err: any) {
    if (err === TIMEOUT) {
      throw err;
    }

    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('Reactive call pages unavailable - not calling Pronto live:', err?.message ?? err);
    throw TIMEOUT;
  }
};

export default GetReactiveCallPages;
